import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAuth } from '../context/AuthContext';
import { useVendor } from '../context/VendorContext';
import { writeFetch } from '../api';
import WithdrawalModal from '../components/Marketplace/Merchant/WithdrawalModal';
import AddProductModal from '../components/Marketplace/Merchant/AddProductModal';
import DetailedPayoutHistory from '../components/Marketplace/Merchant/DetailedPayoutHistory';
import SalesAnalyticsTab from '../components/Marketplace/Merchant/SalesAnalyticsTab';
import MerchantSettingsTab from '../components/Marketplace/Merchant/MerchantSettingsTab';
import TrustBadgesTab from '../components/Marketplace/Merchant/TrustBadgesTab';
import MerchantApplicationModal from '../components/Modals/MerchantApplicationModal';
import ListingsTab from '../components/Marketplace/Merchant/ListingsTab';
import ProductsTab from '../components/Marketplace/Merchant/ProductsTab';

const {
  FiShoppingBag, FiDollarSign, FiStar, FiSettings, FiBriefcase,
  FiClock, FiActivity, FiBarChart2, FiPackage, FiShield, FiCalendar
} = FiIcons;

const TABS = [
  { id: 'overview',      label: 'Overview',         icon: FiActivity   },
  { id: 'products',      label: 'Products',         icon: FiPackage    },
  { id: 'listings',      label: 'Listings',         icon: FiBriefcase  },
  { id: 'trust_badges',  label: 'Trust Badges',     icon: FiShield     },
  { id: 'analytics',     label: 'Sales Analytics',  icon: FiBarChart2  },
  { id: 'history',       label: 'Payout History',   icon: FiDollarSign },
  { id: 'settings',      label: 'Settings',         icon: FiSettings   },
];

const BOOKINGS_TABLE = 'service_bookings_1777866100042';

const MerchantPortal = () => {
  const { user, permissions, effectiveRole } = useAuth();
  const { vendorData } = useVendor();
  const [activeTab, setActiveTab] = useState('overview');
  const [isWithdrawOpen, setIsWithdrawOpen] = useState(false);
  const [isAddProductOpen, setIsAddProductOpen] = useState(false);
  const [isApplyOpen, setIsApplyOpen] = useState(false);
  
  const [appStatus, setAppStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [recentBookings, setRecentBookings] = useState([]);
  const [loadingRecentBookings, setLoadingRecentBookings] = useState(true);
  const [recentBookingsError, setRecentBookingsError] = useState('');

  useEffect(() => {
    const checkStatus = async () => {
      if (!user) {
        setLoadingStatus(false);
        return;
      }
      try {
        const mRes = await writeFetch(`read/merchant_onboarding_codes_1776600001?limit=100`);
        const mData = await mRes.json();
        if (mData.status === 'success' && mData.data?.length > 0) {
          const myCode = mData.data.find(m => String(m.owner_id) === String(user.id));
          if (myCode && myCode.status === 'active') {
            setAppStatus('approved');
            setLoadingStatus(false);
            return;
          }
        }

        const aRes = await writeFetch(`read/merchant_applications_1741165000?limit=100`);
        const aData = await aRes.json();
        if (aData.status === 'success' && aData.data?.length > 0) {
          const myApps = aData.data.filter(a => String(a.owner_id) === String(user.id)).sort((a,b) => new Date(b.created_at) - new Date(a.created_at));
          if (myApps.length > 0) {
            setAppStatus(myApps[0].status);
            setLoadingStatus(false);
            return;
          }
        }
        setAppStatus('none');
      } catch (e) {
        setAppStatus('none');
      }
      setLoadingStatus(false);
    };
    checkStatus();
  }, [user]);

  useEffect(() => {
    if (!user || activeTab !== 'overview') return;
    setLoadingRecentBookings(true);
    setRecentBookingsError('');
    const fetchRecentActivity = async () => {
      try {
        const res = await writeFetch(`read/${BOOKINGS_TABLE}?limit=100`);
        const data = await res.json();
        let allBookings = [];
        if (data.status === 'success' && data.data?.length > 0) {
          allBookings = data.data.filter(b => String(b.merchant_id) === String(user.id));
        }
        
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith('mock_bookings_')) {
            try {
              const mockBookings = JSON.parse(localStorage.getItem(key));
              if (Array.isArray(mockBookings)) {
                allBookings = [...allBookings, ...mockBookings.filter(b => String(b.merchant_id) === String(user.id) && !allBookings.find(a => a.id === b.id))];
              }
            } catch(e) {}
          }
        }
        
        allBookings.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setRecentBookings(allBookings.slice(0, 5));
      } catch (e) {
        console.error("Error fetching recent activity:", e);
        setRecentBookingsError('Could not load recent activity. Please try again.');
      } finally {
        setLoadingRecentBookings(false);
      }
    };
    fetchRecentActivity();
  }, [user, activeTab]);

  const isMerchant = permissions?.isMerchant?.() || effectiveRole === 'merchant' || appStatus === 'approved';
  const isAdmin    = permissions?.canManageMerchants?.();

  if (loadingStatus) {
    return (
      <div className="pt-32 min-h-screen bg-brand-light flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-accent-gold border-t-accent-navy rounded-full animate-spin" />
      </div>
    );
  }

  if (!isMerchant && !isAdmin) {
    if (appStatus === 'pending') {
      return (
        <div className="pt-32 min-h-screen bg-brand-light flex flex-col items-center justify-center p-6">
          <div className="max-w-md w-full bg-white p-12 rounded-[40px] border border-brand-dark text-center shadow-xl">
             <div className="w-20 h-20 bg-amber-50 text-amber-500 rounded-3xl flex items-center justify-center mx-auto mb-6"><SafeIcon icon={FiClock} className="w-10 h-10" /></div>
             <h2 className="text-2xl font-serif text-accent-navy mb-4">Application Pending</h2>
             <p className="text-text-muted text-sm font-light mb-8">Your merchant application is currently under review by our team. You will be notified via email once approved.</p>
             <button onClick={() => window.location.hash = '/dashboard'} className="w-full py-4 bg-accent-gold text-accent-navy rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-blue-600 hover:text-white transition-all">Return to Dashboard</button>
          </div>
        </div>
      );
    }

    return (
      <div className="pt-32 min-h-screen bg-brand-light flex flex-col items-center justify-center p-6">
        <div className="max-w-md w-full bg-white p-12 rounded-[40px] border border-brand-dark text-center shadow-xl">
           <div className="w-20 h-20 bg-red-50 text-red-400 rounded-3xl flex items-center justify-center mx-auto mb-6"><SafeIcon icon={FiShield} className="w-10 h-10" /></div>
           <h2 className="text-2xl font-serif text-accent-navy mb-4">Access Restricted</h2>
           <p className="text-text-muted text-sm font-light mb-8">You must be an approved merchant to access this portal.</p>
           <button onClick={() => setIsApplyOpen(true)} className="w-full py-4 bg-accent-gold text-accent-navy rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-blue-600 hover:text-white transition-all">Apply Now</button>
        </div>
        <MerchantApplicationModal isOpen={isApplyOpen} onClose={() => { setIsApplyOpen(false); setAppStatus('pending'); }} />
      </div>
    );
  }

  return (
    <div className="pt-32 min-h-screen bg-brand-light pb-20 overflow-x-hidden">
      <div className="container mx-auto px-6">
        <div className="bg-white rounded-[40px] p-8 md:p-12 border border-brand-dark shadow-sm mb-10 flex flex-col md:flex-row justify-between items-center gap-8 relative overflow-hidden">
          <div className="flex items-center gap-6 relative z-10">
            <div className="w-16 h-16 bg-accent-navy rounded-3xl flex items-center justify-center text-white shadow-xl">
              <SafeIcon icon={FiBriefcase} className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl font-serif text-accent-navy">{vendorData.shopName}</h1>
              <p className="text-text-muted text-sm mt-1">Verified Merchant Portal</p>
            </div>
          </div>
          <div className="flex gap-3 flex-wrap relative z-10">
            <button onClick={() => setIsWithdrawOpen(true)} className="px-5 py-3 border-2 border-brand-dark bg-white rounded-xl text-[10px] font-black uppercase tracking-widest text-text-muted hover:border-accent-gold transition-all">Withdraw</button>
            <button onClick={() => { setActiveTab('products'); setIsAddProductOpen(true); }} className="px-6 py-3 bg-accent-gold text-accent-navy rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-blue-600 hover:text-white transition-all shadow-lg">Add Product</button>
          </div>
        </div>

        <div className="flex gap-1 mb-8 border-b border-brand-dark/30 overflow-x-auto hide-scrollbar sticky top-24 bg-brand-light z-40 py-2">
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-6 py-4 text-[10px] font-black uppercase tracking-widest transition-all relative whitespace-nowrap ${activeTab === tab.id ? 'text-accent-gold' : 'text-text-muted hover:text-accent-navy'}`}>
              <SafeIcon icon={tab.icon} className="w-3.5 h-3.5" />
              {tab.label}
              {activeTab === tab.id && <motion.div layoutId="mTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-accent-gold rounded-t-full" />}
            </button>
          ))}
        </div>

        <motion.div key={activeTab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="min-h-[60vh]">
          {activeTab === 'overview' && (
            <div className="space-y-10">
               <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
                  {[
                    { label: 'Total Sales', value: vendorData.totalSales, icon: FiShoppingBag, color: 'text-blue-500', bg: 'bg-blue-50' },
                    { label: 'Balance', value: `$${vendorData.balance}`, icon: FiDollarSign, color: 'text-green-500', bg: 'bg-green-50' },
                    { label: 'Rating', value: vendorData.rating, icon: FiStar, color: 'text-accent-gold', bg: 'bg-brand-light' },
                    { label: 'Pending', value: `$${vendorData.pendingBalance}`, icon: FiClock, color: 'text-purple-500', bg: 'bg-purple-50' },
                  ].map(s => (
                    <div key={s.label} className="bg-white p-7 rounded-[32px] border border-brand-dark shadow-sm">
                      <div className={`w-12 h-12 ${s.bg} ${s.color} rounded-2xl flex items-center justify-center mb-5`}><SafeIcon icon={s.icon} className="w-6 h-6" /></div>
                      <h3 className="text-3xl font-serif text-accent-navy mb-1">{s.value}</h3>
                      <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">{s.label}</p>
                    </div>
                  ))}
               </div>
               <div className="bg-white p-8 rounded-[40px] border border-brand-dark shadow-sm">
                 <h3 className="text-2xl font-serif text-accent-navy mb-6">Recent Activity</h3>
                 {loadingRecentBookings && (
                   <div role="status" className="py-10 flex items-center justify-center gap-3 text-sm text-text-muted">
                     <span className="w-4 h-4 border-2 border-accent-gold/30 border-t-accent-gold rounded-full animate-spin" />
                     Loading recent activity…
                   </div>
                 )}
                 {recentBookingsError && (
                   <div role="alert" className="py-10 text-center bg-red-50 rounded-[24px] border border-red-200">
                     <p className="text-sm text-red-700">{recentBookingsError}</p>
                   </div>
                 )}
                 {!loadingRecentBookings && !recentBookingsError && (
                   <>
                 {recentBookings.length === 0 ? (
                   <div className="py-10 text-center bg-brand-light rounded-[24px] border border-dashed border-brand-dark">
                     <SafeIcon icon={FiActivity} className="w-10 h-10 text-text-muted mx-auto mb-3" />
                     <p className="text-text-muted text-sm font-light italic">Your latest transactions and bookings will appear here.</p>
                   </div>
                 ) : (
                   <div className="space-y-4">
                     {recentBookings.map(booking => (
                       <div key={booking.id} className="flex justify-between items-center p-5 border border-brand-light rounded-2xl hover:border-accent-gold transition-colors bg-brand-light/30">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-white rounded-xl shadow-sm flex items-center justify-center text-accent-navy">
                              <SafeIcon icon={FiCalendar} className="w-5 h-5" />
                            </div>
                            <div>
                              <p className="font-bold text-accent-navy">{booking.listing_title || 'Service Booking'}</p>
                              <p className="text-xs text-text-muted mt-1">{booking.customer_name} • {new Date(booking.created_at).toLocaleDateString()}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className={`text-[9px] font-black uppercase tracking-widest px-3 py-1 rounded-full inline-block ${
                              booking.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                              booking.status === 'pending_merchant' || booking.status === 'approved' ? 'bg-blue-100 text-blue-700' :
                              booking.status === 'cancelled' || booking.status === 'rejected' ? 'bg-gray-100 text-gray-500' :
                              'bg-amber-100 text-amber-700'
                            }`}>
                              {booking.status.replace('_', ' ')}
                            </p>
                            {booking.price > 0 && <p className="text-sm font-bold text-green-600 mt-1">${parseFloat(booking.price).toFixed(2)}</p>}
                          </div>
                       </div>
                     ))}
                   </div>
                 )}
                   </>
                 )}
               </div>
            </div>
          )}
          {activeTab === 'products'     && <ProductsTab />}
          {activeTab === 'listings'     && <ListingsTab />}
          {activeTab === 'trust_badges' && <TrustBadgesTab />}
          {activeTab === 'analytics'    && <SalesAnalyticsTab vendorData={vendorData} />}
          {activeTab === 'history'      && <DetailedPayoutHistory payouts={vendorData.payoutHistory} />}
          {activeTab === 'settings'     && <MerchantSettingsTab />}
        </motion.div>
      </div>

      <WithdrawalModal isOpen={isWithdrawOpen} onClose={() => setIsWithdrawOpen(false)} />
      <AddProductModal isOpen={isAddProductOpen} onClose={() => setIsAddProductOpen(false)} onSuccess={() => window.dispatchEvent(new Event('product_added'))} />
    </div>
  );
};

export default MerchantPortal;